import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { novels } from "@/mocks/novels";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const novel = novels.find((n) => n.id === Number(id));

  if (!novel) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">
              文章未找到
            </h1>
            <Link
              to="/"
              className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedNovels = novels
    .filter((n) => n.slug === novel.slug && n.id !== novel.id)
    .slice(0, 3);

  const formatReads = (reads: number) => {
    if (reads >= 10000) return `${(reads / 10000).toFixed(0)}万`;
    return reads.toString();
  };

  const breadcrumbPath = [
    { label: "首页", path: "/" },
    { label: `分类：${novel.category}`, path: `/category/${novel.slug}` },
    { label: novel.title, path: undefined },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb customPath={breadcrumbPath} />

        <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "Article",
                  headline: novel.title,
                  author: { "@type": "Person", name: novel.author },
                  description: novel.description,
                  datePublished: "2026-01-15",
                  publisher: {
                    "@type": "Organization",
                    name: "po18脸红心跳",
                    url: "https://po18-faceheart.com",
                  },
                }),
              }}
            />

            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-40 md:w-48 rounded-lg overflow-hidden">
                  <img
                    src={novel.coverImage}
                    alt={novel.title}
                    title={`${novel.title} - po18言情小说推荐`}
                    className="w-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  {novel.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-3">
                  作者：<strong>{novel.author}</strong> · 分类：{novel.category}
                </p>

                <div className="flex items-center gap-4 mb-4">
                  <span className="flex items-center gap-1 text-accent-500">
                    <i className="ri-star-fill"></i>
                    <span className="font-semibold">{novel.rating}</span>
                  </span>
                  <span className="text-sm text-foreground-400">
                    {formatReads(novel.reads)} 阅读
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-wrap mb-4">
                  {novel.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-700 leading-relaxed">
                  {novel.description}
                </p>
              </div>
            </div>

            <div className="mt-10 border-t border-background-200/70 pt-8">
              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: novel.content }}
              />
            </div>

            {novel.faq && novel.faq.length > 0 && (
              <div className="mt-10 border-t border-background-200/70 pt-8">
                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                      "@context": "https://schema.org",
                      "@type": "FAQPage",
                      mainEntity: novel.faq.map((item) => ({
                        "@type": "Question",
                        name: item.question,
                        acceptedAnswer: {
                          "@type": "Answer",
                          text: item.answer,
                        },
                      })),
                    }),
                  }}
                />
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                  常见问题
                </h2>
                <div className="space-y-4">
                  {novel.faq.map((item, idx) => (
                    <div
                      key={idx}
                      className="p-4 rounded-lg bg-background-100"
                    >
                      <h3 className="text-sm font-semibold text-foreground-800 mb-1">
                        Q: {item.question}
                      </h3>
                      <p className="text-sm text-foreground-600">
                        A: {item.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {relatedNovels.length > 0 && (
              <div className="mt-10 border-t border-background-200/70 pt-8">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedNovels.map((rn) => (
                    <Link
                      key={rn.id}
                      to={`/article/${rn.id}`}
                      className="group flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                    >
                      <div className="w-12 h-16 flex-shrink-0 rounded overflow-hidden">
                        <img
                          src={rn.coverImage}
                          alt={rn.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors truncate">
                          {rn.title}
                        </p>
                        <p className="text-xs text-foreground-500">
                          {rn.author}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}